/**
 * Public type for the Hub space density (spacing) customization.
 *
 * Values mirror the backend `HelpCenterSpaceDensity` GraphQL enum exactly
 * (uppercase), so there is no case-mapping at the read/write boundaries.
 *
 * The chosen density will control the vertical rhythm
 * between every element on the hub page and, eventually, the spacing inside
 * elements.
 */
export type SpaceDensity = 'DEFAULT' | 'COMFORTABLE' | 'BREEZY';

/**
 * Ordered list of selectable densities, in the order they are shown in the
 * config panel control
 */
export const SPACE_DENSITIES: readonly SpaceDensity[] = ['DEFAULT', 'COMFORTABLE', 'BREEZY'];

/**
 * The density selected when the admin has made no choice and no value is
 * persisted on the backend.
 */
export const DEFAULT_SPACE_DENSITY: SpaceDensity = 'DEFAULT';

/**
 * The CSS custom property exposed by `@atlaskit/editor-common` /
 * `@atlaskit/editor-core` that drives the vertical margin between editor block
 * elements (paragraphs, lists, panels, code blocks, blockquotes, smart cards
 * and extension wrappers). Setting it on an ancestor of the editor content
 * overrides that spacing; leaving it unset falls back to the editor's original
 * value, so this is a no-op for every other consumer.
 *
 * Kept as a local constant (rather than importing from `@atlaskit/editor-common`)
 * to avoid pulling a heavy editor dependency into this common package. It must
 * stay in sync with `EXTENSION_BLOCK_SPACING_VAR` in editor-common.
 */
export const EDITOR_BLOCK_SPACING_VAR = '--ak-editor-extension-block-spacing';

/**
 * Vertical spacing applied between hub elements for each density, driving the
 * editor block-spacing CSS variable. Values come from the Hub space density
 * Figma spec (component top/bottom spacing):
 * - Default: 12px
 * - Comfortable: 24px
 * - Breezy: 32px
 */
export const SPACE_DENSITY_BLOCK_SPACING: Record<SpaceDensity, string> = {
	DEFAULT: '12px',
	COMFORTABLE: '24px',
	BREEZY: '32px',
};

/**
 * Returns the editor block-spacing value for a given density.
 */
export const getBlockSpacingForDensity = (density: SpaceDensity): string =>
	SPACE_DENSITY_BLOCK_SPACING[density];

/**
 * The horizontal gap (in pixels) between cards/items rendered inside hub
 * extensions (e.g. JSM Help cards, links-menu items) for each density.
 *
 * This gap feeds the card grid's width/columns calculation, so it is a plain
 * number (not a CSS value). When space density is disabled or unset, the card
 * grid keeps its original 16px gap — so this is a no-op for existing hubs.
 */
export const SPACE_DENSITY_CARD_GAP: Record<SpaceDensity, number> = {
	DEFAULT: 12,
	COMFORTABLE: 24,
	BREEZY: 32,
};

/**
 * Returns the horizontal card gap (px) for a given density.
 */
export const getCardGapForDensity = (density: SpaceDensity): number =>
	SPACE_DENSITY_CARD_GAP[density];
